import React from 'react';
import { Checkbox, Radio, Tooltip } from 'flowbite-react';
import { StrategyMapListItem } from '../../services/strategyMap';
import { getEntityTypeDisplay } from '../../utils/entityDisplayUtils';
import { getDashboardStatusColor } from '../../constants/integrationTypes';
import './EntityItem.css';
import TooltipWrapper from '../../shared/atoms/TooltipWrapper';

interface EntityItemProps {
  item: StrategyMapListItem;
  isSelected: boolean;
  onToggle: () => void;
  isDisabled?: boolean;
  readOnly?: boolean;
  tooltipMessage?: React.ReactNode;
  extraInfo?: React.ReactNode;
  selectType?: 'single' | 'multiple';
}

const EntityItem: React.FC<EntityItemProps> = ({ 
  item, 
  isSelected, 
  onToggle,
  isDisabled = false,
  readOnly = false,
  tooltipMessage,
  extraInfo,
  selectType = 'multiple'
}) => {

  // Map roadmap/project to epic/feature for consistent icons

  const entityInfo = getEntityTypeDisplay(item.entity_type, item.type);
  const Icon = entityInfo.icon;

  
  const statusColorClass = item.status 
    ? getDashboardStatusColor(item.status) 
    : 'bg-light-text-muted';

  const content = (
    <div
      className={`entity-item ${isSelected ? "entity-item--selected" : ""} ${isDisabled ? "entity-item--disabled" : ""} ${readOnly ? "popup-readonly" : ""}`}
      onClick={isDisabled || readOnly ? undefined : onToggle}
    >
      <div className="entity-item__status-container">
        {/* @ts-ignore */}
        <Icon size={20} className={entityInfo.iconClass} />
        <span className={`entity-item__status-dot ${statusColorClass}`} />
      </div>

      <div className="entity-item__content">
        <div className="entity-item__info">
          <div className="entity-item__type">
            {entityInfo.label} {item?.source ? `(${item?.source})` : ""}
          </div>
          <div className="entity-item__name" title={item?.name ?? ""}>
            {item?.name ?? ""}
          </div>
          <div className="entity-item__disabled-reason" title={isDisabled && tooltipMessage ? String(tooltipMessage) : undefined}>
            {isDisabled && tooltipMessage ? <>⚠ {tooltipMessage}</> : null}
          </div>
        </div>

        <div className="entity-item__stats">
          {extraInfo}
          <div className="entity-item__stat_box entity-item__stat-group">
            <div className="entity-item__stat-label">Status</div>
            <div className="entity-item__stat-value">
              {item.status?.replace("_", " ") || "-"}
            </div>
          </div>
          <div className="entity-item__stat_box entity-item__stat-group">
            <div className="entity-item__stat-label">Progress</div>
            <div className="entity-item__stat-value">{item.progress ?? 0}%</div>
          </div>
        </div>

        <div
          className={`entity-item__checkbox ${readOnly ? "opacity-50" : ""}`}
        >
          {selectType === 'single' ? (
             <Radio
               checked={isSelected}
               onChange={isDisabled || readOnly ? undefined : onToggle}
               onClick={(e) => e.stopPropagation()}
               disabled={isDisabled || readOnly}
               readOnly={readOnly}
             />
          ) : (
            <Checkbox
              checked={isSelected}
              onChange={isDisabled || readOnly ? undefined : onToggle}
              onClick={(e) => e.stopPropagation()}
              disabled={isDisabled || readOnly}
            />
          )}
        </div>
      </div>
    </div>
  );

  if (tooltipMessage) {
    return (
      <div className="w-full">
        <TooltipWrapper message={tooltipMessage} placement="top">
          {content}
        </TooltipWrapper>
      </div>
    );
  }

  return content;
};

export default EntityItem;
